import React, { useCallback, useMemo, useState } from 'react';

import { Button } from 'react-bootstrap-5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileSpreadsheet } from '@fortawesome/pro-regular-svg-icons';
import { faFileDownload, faFileExport, faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { formatDate, SHORT_DATE_FORMAT } from 'helpers/formatters/date_time_formats';

import EmptyState from '../empty_state';
import { dataExportDownloadUrl } from '../../helpers/prospect_api';
import Paginator from '../paginator';
import type { DataExport } from 'bundles/connect/types';

type Props = {
  exports: DataExport[];
}

const PER_PAGE = 5;
export default function ExportList({ exports }: Props) {
  const [page, setPage] = useState(1);

  const paginatedResults = useMemo(() => {
    return exports.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  }, [exports, page]);

  const link = useCallback((ex) => {
    if (ex.dataFileSize) return (
      <Button data-turbo={false} href={dataExportDownloadUrl(ex.id)} variant="link">
        <FontAwesomeIcon icon={faFileDownload}/>
        Download
      </Button>
    );

    if (ex.exportError) return (
      <Button className="text-danger" disabled variant="link">
        Error
      </Button>
    );

    return (
      <Button className="text-muted" disabled variant="link">
        <FontAwesomeIcon icon={faSpinner} spin/>
        Exporting
      </Button>
    );
  }, []);

  return (
    <>
      {exports.length > 0 ? (
        <>
          {paginatedResults.map(ex => (
            <div className="d-flex py-2 gap-2 border-bottom" key={ex.id}>
              <FontAwesomeIcon className="align-self-start py-1" icon={faFileSpreadsheet} size="lg" />
              <div className="flex-grow-1">
                <div className="fw-bold">{ex.name}</div>
                <div className="text-muted small">
                  Exported on {formatDate(ex.createdAt, SHORT_DATE_FORMAT)}
                  {ex.lastDownloaded && (
                    <span> - Last downloaded {formatDate(ex.lastDownloaded, SHORT_DATE_FORMAT)}</span>
                  )}
                </div>
              </div>
              <div className="text-end">
                {link(ex)}
              </div>
            </div>
          
          ))}
          {exports.length > PER_PAGE && (
            <div className="d-flex justify-content-center">
              <Paginator currentPage={page} perPage={PER_PAGE} total={exports.length} onSelect={setPage}/>
            </div>
          )}
        </>
      ) : (
        <EmptyState icon={faFileExport} title="You don't have any data exports"/>
      )}
    </>
  );
}
